import type {
  MemberAnnotationCreateInput,
  MemberAnnotationResponse,
  MemberAnnotationUpdateInput,
  MemberUnassignedAnnotationsResponse,
} from '@govinity/validation/public';

import { ReadOnlyApi } from '../base';

/**
 * API class for member meeting annotations endpoints.
 * Provides CRUD operations for annotations on agenda section items.
 */
class MemberAnnotationsApi extends ReadOnlyApi<
  MemberAnnotationResponse,
  MemberAnnotationResponse,
  never,
  MemberAnnotationResponse[]
> {
  private meetingId: number = 0;

  getPath() {
    return `/members/meetings/${this.meetingId}/annotations`;
  }

  /**
   * Sets the context for annotation operations.
   */
  forMeeting(meetingId: number) {
    this.meetingId = meetingId;
    return this;
  }

  /**
   * Retrieves all annotations for an agenda section item.
   */
  async getAnnotations(meetingId: number, agendaSectionItemId: number) {
    const { data } = await this.request<MemberAnnotationResponse[]>({
      method: 'GET',
      url: `/members/meetings/${meetingId}/annotations`,
      params: { agendaSectionItemId },
    });

    return data;
  }

  /**
   * Creates a new annotation.
   */
  async create(meetingId: number, body: MemberAnnotationCreateInput) {
    const { data } = await this.request<MemberAnnotationResponse>({
      method: 'POST',
      url: `/members/meetings/${meetingId}/annotations`,
      data: body,
    });

    return data;
  }

  /**
   * Updates an annotation's content, re-anchors an unassigned one, or both.
   */
  async update(meetingId: number, annotationId: number, body: MemberAnnotationUpdateInput) {
    const { data } = await this.request<MemberAnnotationResponse>({
      method: 'PUT',
      url: `/members/meetings/${meetingId}/annotations/${annotationId}`,
      data: body,
    });

    return data;
  }

  /**
   * Lists the member's annotations on the meeting that lost their agenda section / item link.
   * The API repairs the unambiguous ones as a side effect, so anything returned needs the member
   * to place it by hand.
   */
  async getUnassigned(meetingId: number) {
    const { data } = await this.request<MemberUnassignedAnnotationsResponse>({
      method: 'GET',
      url: `/members/meetings/${meetingId}/annotations/unassigned`,
    });

    return data;
  }

  /**
   * Deletes an annotation.
   */
  async delete(meetingId: number, annotationId: number) {
    await this.request({
      method: 'DELETE',
      url: `/members/meetings/${meetingId}/annotations/${annotationId}`,
    });
  }
}

export default MemberAnnotationsApi;
